import { motion } from 'framer-motion';
import {
  FileText,
  Clock,
  ArrowRight,
  Stethoscope,
  Heart,
  Wind,
  Activity,
  Brain,
  Dna,
  Timer,
  BarChart3,
  CheckCircle2,
  Sparkles,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ─── Tipos de avaliação agrupados por especialidade ─── */
const specialties = [
  {
    area: 'Respiratória',
    icon: Wind,
    color: 'cyan',
    bgGradient: 'from-cyan-500/20 to-cyan-600/5',
    borderColor: 'border-cyan-500/30',
    iconBg: 'bg-cyan-500/20',
    iconColor: 'text-cyan-400',
    tests: [
      { name: 'Espirometria', detail: 'FVC, VEF1, PFE — equações Pereira et al. (2007) para pop. brasileira' },
      { name: 'CAT', detail: 'COPD Assessment Test — impacto da DPOC na vida diária' },
      { name: 'mMRC', detail: 'Escala de dispneia Modified Medical Research Council' },
      { name: 'SGRQ', detail: 'Saint George — qualidade de vida respiratória' },
      { name: 'PImax / S-Index', detail: 'Força muscular respiratória (Neder et al., 1999)' },
      { name: 'PFSDQ-M', detail: 'Status funcional e dispneia' },
    ],
  },
  {
    area: 'Cardiovascular',
    icon: Heart,
    color: 'rose',
    bgGradient: 'from-rose-500/20 to-rose-600/5',
    borderColor: 'border-rose-500/30',
    iconBg: 'bg-rose-500/20',
    iconColor: 'text-rose-400',
    tests: [
      { name: 'TCPE / CPET', detail: 'Teste Cardiopulmonar de Esforço — VO₂, limiares, zonas de treino' },
      { name: 'TC6', detail: 'Teste de Caminhada de 6 min (Enright & Sherrill, 1998)' },
      { name: 'MLHFQ', detail: 'Minnesota — qualidade de vida na insuficiência cardíaca' },
    ],
  },
  {
    area: 'Funcional & Oncológica',
    icon: Activity,
    color: 'violet',
    bgGradient: 'from-violet-500/20 to-violet-600/5',
    borderColor: 'border-violet-500/30',
    iconBg: 'bg-violet-500/20',
    iconColor: 'text-violet-400',
    tests: [
      { name: 'STS 5x / 30s / 1min', detail: 'Sentar-Levantar — força funcional de MMII' },
      { name: 'LCADL', detail: 'London Chest — atividades de vida diária' },
      { name: 'HADS', detail: 'Ansiedade e depressão hospitalar' },
      { name: 'ECOG / Karnofsky', detail: 'Performance status oncológico' },
    ],
  },
];

/* ─── Métricas de impacto ─── */
const impactMetrics = [
  {
    icon: Timer,
    value: '< 30s',
    label: 'para gerar um laudo completo',
    description: 'O que levaria horas de cálculos, referências e redação é entregue em segundos.',
  },
  {
    icon: FileText,
    value: '13',
    label: 'tipos de avaliação',
    description: 'Cobrindo doenças respiratórias, cardiovasculares, metabólicas e oncológicas.',
  },
  {
    icon: BarChart3,
    value: '100%',
    label: 'interpretação automatizada',
    description: 'Classificação, severidade, limitações e recomendações — tudo gerado pelo sistema.',
  },
  {
    icon: Sparkles,
    value: 'PDF',
    label: 'laudo profissional',
    description: 'Com gráficos, comparação histórica e referências bibliográficas validadas.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const AssessmentShowcase = () => {
  return (
    <section className="py-20 md:py-28 bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 h-[500px] w-[500px] rounded-full bg-cyan-500/5 blur-[140px]" />
        <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-violet-500/5 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[300px] rounded-full bg-rose-500/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* ─── Header ─── */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 mb-6"
          >
            <Stethoscope size={16} className="text-cyan-400" />
            <span className="text-sm font-medium text-cyan-300">
              Diferencial exclusivo
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 leading-tight"
          >
            Laudos com interpretação clínica em{' '}
            <span className="bg-gradient-to-r from-cyan-300 via-violet-300 to-rose-300 bg-clip-text text-transparent">
              segundos, não horas
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
          >
            O RevaHub oferece <strong className="text-white">13 avaliações clínicas</strong> específicas
            para doenças respiratórias, cardiovasculares, metabólicas e oncológicas — com geração
            automática de laudos profissionais, gráficos de evolução e interpretação clínica
            baseada em equações validadas internacionalmente.
          </motion.p>
        </div>

        {/* ─── Comparação Antes vs. Depois ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <div className="grid md:grid-cols-2 gap-4">
            {/* ANTES */}
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={20} className="text-red-400" />
                <span className="text-sm font-semibold text-red-400 uppercase tracking-wide">
                  Sem o RevaHub
                </span>
              </div>
              <ul className="space-y-3">
                {[
                  'Calcular valores preditos manualmente (tabelas, fórmulas)',
                  'Buscar referências bibliográficas para cada teste',
                  'Redigir interpretação clínica do zero a cada avaliação',
                  'Criar gráficos de evolução em planilhas separadas',
                  'Formatar e imprimir laudos em Word ou PDF genérico',
                  'Comparar resultados anteriores folheando prontuários',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-400">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-400/60 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-red-500/10">
                <span className="text-2xl font-display font-bold text-red-400">2-4 horas</span>
                <span className="ml-2 text-sm text-slate-500">por laudo completo</span>
              </div>
            </div>

            {/* DEPOIS */}
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={20} className="text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-400 uppercase tracking-wide">
                  Com o RevaHub
                </span>
              </div>
              <ul className="space-y-3">
                {[
                  'Valores preditos calculados automaticamente (Pereira, Neder, Enright)',
                  'Referências bibliográficas incluídas no laudo',
                  'Interpretação clínica narrativa gerada pelo sistema',
                  'Gráficos profissionais com evolução temporal integrada',
                  'PDF de alta qualidade com identidade visual da clínica',
                  'Comparação automática com todas as avaliações anteriores',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <CheckCircle2 size={14} className="mt-0.5 text-emerald-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-emerald-500/10">
                <span className="text-2xl font-display font-bold text-emerald-400">
                  &lt; 30 segundos
                </span>
                <span className="ml-2 text-sm text-slate-500">laudo completo com 1 clique</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ─── Métricas de impacto ─── */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20 max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {impactMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <motion.div
                key={metric.label}
                variants={itemVariants}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-center hover:border-white/20 transition-colors"
              >
                <Icon size={24} className="mx-auto mb-3 text-cyan-400" />
                <div className="text-3xl font-display font-bold text-white mb-1">
                  {metric.value}
                </div>
                <div className="text-sm font-medium text-slate-300 mb-2">
                  {metric.label}
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {metric.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Cards por especialidade ─── */}
        <div className="max-w-6xl mx-auto mb-16">
          <motion.h3
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl md:text-2xl font-display font-bold text-white text-center mb-10"
          >
            Avaliações disponíveis por especialidade
          </motion.h3>

          <div className="grid md:grid-cols-3 gap-6">
            {specialties.map((specialty, sIdx) => {
              const Icon = specialty.icon;
              return (
                <motion.div
                  key={specialty.area}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: sIdx * 0.1 }}
                  className={`rounded-xl border ${specialty.borderColor} bg-gradient-to-b ${specialty.bgGradient} p-6`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className={`h-10 w-10 rounded-lg ${specialty.iconBg} flex items-center justify-center`}
                    >
                      <Icon className={`h-5 w-5 ${specialty.iconColor}`} />
                    </div>
                    <h4 className="text-lg font-display font-bold text-white">
                      {specialty.area}
                    </h4>
                  </div>

                  <div className="space-y-3">
                    {specialty.tests.map((test) => (
                      <div key={test.name} className="group">
                        <div className="flex items-start gap-2">
                          <CheckCircle2
                            size={14}
                            className={`mt-0.5 shrink-0 ${specialty.iconColor}`}
                          />
                          <div>
                            <span className="text-sm font-medium text-slate-200">
                              {test.name}
                            </span>
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                              {test.detail}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ─── Mockup do laudo ─── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-transparent to-violet-500/10 blur-2xl" />

            <div className="relative rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur-sm overflow-hidden">
              {/* Barra de título */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 bg-white/[0.02]">
                <div className="flex gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-red-400/80" />
                  <span className="h-3 w-3 rounded-full bg-yellow-400/80" />
                  <span className="h-3 w-3 rounded-full bg-green-400/80" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="h-6 rounded-md bg-white/5 flex items-center px-3 text-xs text-slate-500">
                    app.revahub.com.br/admin/avaliacoes/cpet/laudo
                  </div>
                </div>
              </div>

              {/* Conteúdo simulado do laudo */}
              <div className="p-6 md:p-8">
                {/* Header do laudo */}
                <div className="text-center mb-6 pb-4 border-b border-white/5">
                  <div className="h-3 w-40 rounded bg-white/10 mx-auto mb-2" />
                  <div className="text-xs text-cyan-400 font-medium tracking-wider uppercase">
                    Relatório — Teste Cardiopulmonar de Esforço (TCPE)
                  </div>
                </div>

                {/* Dados do paciente */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {['Paciente', 'Idade', 'Sexo', 'Data'].map((label) => (
                    <div key={label} className="rounded-lg bg-white/[0.03] p-2.5">
                      <div className="text-[10px] text-slate-500 mb-1">{label}</div>
                      <div className="h-2.5 w-3/4 rounded bg-white/10" />
                    </div>
                  ))}
                </div>

                {/* Métricas principais */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { label: 'VO₂ pico', value: '18.4 mL/kg/min', status: 'Moderadamente reduzida', statusColor: 'text-yellow-400' },
                    { label: 'FC máx', value: '142 bpm (87% pred.)', status: 'Adequada', statusColor: 'text-emerald-400' },
                    { label: 'VE/VCO₂ slope', value: '32.1', status: 'Limítrofe', statusColor: 'text-yellow-400' },
                  ].map((metric) => (
                    <div key={metric.label} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                      <div className="text-[10px] text-slate-500 mb-1">{metric.label}</div>
                      <div className="text-sm font-bold text-white mb-0.5">{metric.value}</div>
                      <div className={`text-[10px] font-medium ${metric.statusColor}`}>
                        {metric.status}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Gráfico simulado */}
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4 mb-6">
                  <div className="text-xs text-slate-400 mb-3 font-medium">
                    Evolução FC e SpO₂ durante o teste
                  </div>
                  <div className="flex items-end gap-1 h-16">
                    {[30, 40, 50, 55, 60, 65, 70, 75, 80, 85, 90, 88, 85, 80, 70, 55].map(
                      (h, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          whileInView={{ height: `${h}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.6 + i * 0.03 }}
                          className="flex-1 rounded-sm bg-gradient-to-t from-rose-500/50 to-rose-400/20"
                        />
                      )
                    )}
                  </div>
                </div>

                {/* Interpretação clínica */}
                <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-cyan-400" />
                    <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wide">
                      Interpretação Clínica (gerada automaticamente)
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Teste cardiopulmonar de esforço realizado em protocolo de rampa incremental.
                    A capacidade aeróbica está moderadamente reduzida, com VO₂ pico de 1.42 L/min
                    (18.4 mL/kg/min), correspondendo a 72% do valor predito. A limitação primária
                    ao exercício é de origem cardiovascular. O limiar anaeróbio (VT1) foi
                    identificado em 48% do VO₂ pico. A eficiência ventilatória (VE/VCO₂ slope =
                    32.1) está limítrofe...
                  </p>
                </div>

                {/* Zonas de treino */}
                <div className="mt-4 grid grid-cols-5 gap-1.5">
                  {[
                    { zone: 'Z1', label: 'Recuperação', color: 'bg-emerald-500/30' },
                    { zone: 'Z2', label: 'Aeróbico', color: 'bg-cyan-500/30' },
                    { zone: 'Z3', label: 'Moderado', color: 'bg-yellow-500/30' },
                    { zone: 'Z4', label: 'Anaeróbico', color: 'bg-orange-500/30' },
                    { zone: 'Z5', label: 'Máximo', color: 'bg-red-500/30' },
                  ].map((z) => (
                    <div key={z.zone} className={`rounded-md ${z.color} p-2 text-center`}>
                      <div className="text-[10px] font-bold text-white">{z.zone}</div>
                      <div className="text-[8px] text-slate-400">{z.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ─── CTA ─── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <Link
            to="/cadastro"
            className="group inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 py-3.5 px-8 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/30"
          >
            Experimente as avaliações gratuitamente
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </Link>
          <p className="mt-4 text-sm text-slate-500">
            14 dias grátis — sem cartão de crédito
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AssessmentShowcase;
